import { Injectable } from '@angular/core';
import { CustomDateTimeByUtcPipe } from '@shared/pipes-shared/pipes';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyPipe } from '@angular/common';
import { ManagementBillModel } from '@data-layer/models/management';
import { forkJoin, Observable, of } from 'rxjs';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { enumPaymentState } from '@data-layer/enums/payments';
import { map } from 'rxjs/operators';
import { BillItemModel } from '@data-layer/models/payments';
import { UtilsHelper } from '@helpers/utils-helper';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';

@Injectable({ providedIn: 'root' })
export class TableGeneratorService {
  constructor(
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private customDateTime: CustomDateTimeByUtcPipe,
    private currency: CurrencyPipe,
  ) {}

  public getManagementBillModelTableRows(list: ManagementBillModel[]): Observable<Array<Array<number | string>>> {
    const result: Array<Observable<Array<string | number>>> = [];
    for (let billIndex: number = 0; billIndex < list.length; billIndex++) {
      result.push(this.getManagementBillModelTableRow(list[billIndex], billIndex + 1));
      const bill: ManagementBillModel = list[billIndex];
      for (let index: number = 0; index < bill.items.length; index++) {
        result.push(this.getManagementBillModelItemTableRow(bill.items[index], index + 1));
      }
    }
    return forkJoin(result);
  }

  public getManagementBillModelTableRow(bill: ManagementBillModel, index: number): Observable<string[]> {
    let stateName = this.translateService.get('PAYMENT_STATE.' + enumPaymentState[bill.state]);

    switch (bill.state) {
      case enumPaymentState.SUCCESS:
        stateName = this.translateService.get('LABELS.PAID');
        break;
      default:
        break;
    }

    return forkJoin({
      billType: this.translateService.get(
        bill.providerType === enumCompanyPaymentType.MONTHLY ? 'PERIOD_PAYMENT_TYPE.MONTHLY' : 'LABELS.INSTANT',
      ),
      stateName,
    }).pipe(
      map((data: any) => [
        index,
        data.billType,
        this.customDateTime.transform(bill.timestamp),
        bill.user.email,
        this.currency.transform(bill.items.reduce((prev: number, item: BillItemModel) => item.price * item.quantity + prev, 0)),
        data.stateName,
        bill.invoice && bill.invoice.documentNumber ? bill.invoice.documentNumber : '',
        bill.providerTransactionId,
      ]),
    );
  }

  public getManagementBillModelItemTableRow(billItem: BillItemModel, index: number): Observable<Array<string | number>> {
    return of([
      '',
      index,
      this.customDateTime.transform(billItem.timestamp),
      [billItem.referenceNumber, billItem.description].join(' - '),
      (billItem.quantity > 1 ? billItem.quantity + ' x ' : '') + this.currency.transform(billItem.price),
    ]);
  }

  public getFiltersForBills(lastSearch: any): Array<{ label: string; value: string }> {
    const filters: Array<{ label: string; value: string }> = [];
    if (this.searchableValue(lastSearch.userName)) {
      filters.push({ label: 'FORM_PLACEHOLDER.BY_USER_NAME', value: lastSearch.userName });
    }
    if (this.searchableValue(lastSearch.dateTimeFrom)) {
      filters.push({ label: 'FORM_PLACEHOLDER.DATE_FROM', value: this.customDateTime.transform(lastSearch.dateTimeFrom) });
    }
    if (this.searchableValue(lastSearch.dateTimeTo)) {
      filters.push({ label: 'FORM_PLACEHOLDER.DATE_TO', value: this.customDateTime.transform(lastSearch.dateTimeTo) });
    }

    return filters;
  }

  private searchableValue(value: any): boolean {
    return !UtilsHelper.isNullOrUndefined(value) && value !== '';
  }
}
